import { forwardRef, useCallback, useMemo, useState } from 'react';
import { Flex, ActionIcon, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
	DocumentSigningScope,
	DocumentWorkflowInstanceSignatureStatus,
	InvestorDocumentWorkflowDocumentRes,
} from '@dealstack/shared/lib/generated';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useInvestorDocumentWorkflowList } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';

import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useNavigateToMessages } from '@dealstack/frontend/app/pages/Project/MIP/Messages/hooks/useNavigateToMessages';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { StyledSignedChip } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/styled';
import {
	mapStatusToChipVariant,
	mapInstanceStatusToChipVariant,
	PreviewDocument,
} from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/types';
import {
	TableTitle,
	InvestorSignatureWrapper,
	DocumentNameCell,
	TruncatedTextCell,
} from './InvestorDocumentWorkflowTable.styles';
import { deriveInvestorSignatureDisplayStatus } from './InvestorDocumentWorkflowTable.utils';
import { WitnessChip } from './WitnessChip';
import { CounterpartySignatureCell } from './CounterpartySignatureCell';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

export const InvestorDocumentWorkflowTable = forwardRef<
	HTMLDivElement,
	{
		getInvestmentReference: (
			investmentWorkflowType: string,
			isReleased: boolean,
			date: string
		) => string;
	}
>(({ getInvestmentReference }, ref) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const navigate = useNavigate();
	const { dealId, mipId, investorId } = useParams();
	const { navigateToMessages } = useNavigateToMessages(investorId);
	const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);

	const dataOperations = useMantineTableDataOperations({
		initialSorting: [{ id: 'investmentReference', desc: true }],
	});
	const { downloadFile } = useDownloadFileUsingFileStorageUrl();

	const documentsQuery = useInvestorDocumentWorkflowList(investorId);
	const documents = documentsQuery.data?.payload?.documents ?? [];

	const getDownloadPreSignedUrl = useMutation({
		mutationFn: DocumentService.getDownloadPreSignedUrl,
	});

	const handlePreviewFile = useCallback(
		(fileId: string, fileName: string) => {
			if (!dealId) {
				return;
			}

			const fileExtension = fileName.split('.').pop();
			setPreviewDocument({ fileId, fileName, fileExtension });

			getDownloadPreSignedUrl.mutate(
				{ dealId, fileId },
				{
					onSuccess: (data) => {
						if (!data.payload?.downloadUrl) {
							showToastV2({ state: 'error', title: 'Preview failed' });
							setPreviewDocument(null);
							return;
						}

						void fetch(data.payload.downloadUrl)
							.then((response) => response.blob())
							.then((blob) => {
								setPreviewDocument((prev) =>
									prev?.fileId === fileId ? { ...prev, fileBlob: blob } : prev
								);
							})
							.catch(() => {
								showToastV2({ state: 'error', title: 'Preview failed' });
								setPreviewDocument(null);
							});
					},
					onError: () => {
						showToastV2({ state: 'error', title: 'Preview failed' });
						setPreviewDocument(null);
					},
				}
			);
		},
		[dealId, getDownloadPreSignedUrl]
	);

	const columns: ExtendedColumnDef<InvestorDocumentWorkflowDocumentRes>[] = useMemo(
		() => [
			createTextColumn({
				id: 'documentName',
				header: () => (
					<Text fw={600}>{tMipManage('investors.expanded_row.contract_name')}</Text>
				),
				accessorKey: 'documentName',
				enableSorting: true,
				minSize: 350,
				cell: ({ row }) => {
					const name = row.original.documentName ?? row.original.fileName ?? '-';
					return (
						<DocumentNameCell>
							<Icon
								variant="document-v3-rounded"
								size="lg"
								style={{ flexShrink: 0, paddingRight: '0.2rem' }}
							/>
							<Text
								size="sm"
								component="span"
								className="document-name-text"
								title={name}
							>
								{name}
							</Text>
						</DocumentNameCell>
					);
				},
			}),
			{
				id: 'investorSignature',
				header: () => (
					<Text fw={600}>{tMipManage('investors.expanded_row.investor_signature')}</Text>
				),
				enableSorting: true,
				size: 100,
				accessorFn: (row) => row.investorSignature.status ?? '',
				cell: ({ row }) => {
					const { signingScope, investorSignature, signatureType } = row.original;
					if (signingScope === DocumentSigningScope.ALL_PARTICIPANTS) {
						const signedCount = investorSignature.signedCount ?? 0;
						const totalCount = investorSignature.totalCount ?? 0;
						const allSigned = totalCount > 0 && signedCount === totalCount;
						return (
							<InvestorSignatureWrapper>
								<ChipsV2
									outline
									size="s"
									color={allSigned ? COLOR.GREEN : COLOR.YELLOW}
									chipTheme={CHIP_THEME.LIGHT}
									prefixIcon="signed"
								>
									{`${signedCount}/${totalCount}`}
								</ChipsV2>
							</InvestorSignatureWrapper>
						);
					}

					const primaryStatus =
						investorSignature.status ?? DocumentWorkflowInstanceSignatureStatus.CREATED;
					const displayStatus = deriveInvestorSignatureDisplayStatus(
						primaryStatus,
						investorSignature.witness
					);
					const variant = mapStatusToChipVariant(displayStatus, signatureType);
					return (
						<InvestorSignatureWrapper>
							<StyledSignedChip variant={variant} />
							{WitnessChip(investorSignature.witness)}
						</InvestorSignatureWrapper>
					);
				},
			},
			{
				id: 'counterpartySignature',
				header: () => (
					<Text fw={600}>
						{tMipManage('investors.expanded_row.counterparty_signature')}
					</Text>
				),
				enableSorting: false,
				size: 100,
				cell: ({ row }) => (
					<CounterpartySignatureCell
						signatures={row.original.counterpartySignatures}
						signatureType={row.original.signatureType}
					/>
				),
			},
			{
				id: 'investmentReference',
				header: () => (
					<Text fw={600}>
						{tMipManage('investors.expanded_row.investment_reference')}
					</Text>
				),
				enableSorting: true,
				accessorFn: (row) => row.investmentWorkflow?.date ?? '',
				minSize: 100,
				size: 100,
				cell: ({ row }) => {
					const text =
						row.original.investmentWorkflow?.investmentWorkflowType &&
						row.original.investmentWorkflow.date
							? getInvestmentReference(
									row.original.investmentWorkflow.investmentWorkflowType,
									!!row.original.investmentWorkflow.isReleased,
									row.original.investmentWorkflow.date
								)
							: '-';
					return (
						<TruncatedTextCell>
							<Text
								size="sm"
								component="span"
								className="truncated-text"
								title={text}
							>
								{text}
							</Text>
						</TruncatedTextCell>
					);
				},
			},
			{
				id: 'state',
				size: 50,
				header: () => <Text fw={600}>{tMipManage('investors.expanded_row.state')}</Text>,
				enableSorting: true,
				accessorFn: (row) => row.workflowStatus,
				cell: ({ row }) => {
					const variant = mapInstanceStatusToChipVariant(row.original.workflowStatus);

					return <StyledSignedChip variant={variant} />;
				},
			},
			{
				id: 'actions',
				header: () => (
					<Flex w="100%" justify="center">
						<Text fw={600}>{tMipManage('investors.expanded_row.actions')}</Text>
					</Flex>
				),
				enableSorting: false,
				size: 190,
				minSize: 190,
				maxSize: 190,
				cell: ({ row }) => (
					<Flex w="100%" gap="xs" justify="center">
						<ActionIcon
							size="lg"
							variant="subtle"
							color="blue"
							title={tMipManage('investors.view_messages')}
							onClick={(event) => {
								event.stopPropagation();
								if (row.original.investmentWorkflow?.investmentWorkflowId) {
									navigateToMessages({
										investmentWorkflowId:
											row.original.investmentWorkflow.investmentWorkflowId,
										documentWorkflowInstanceId:
											row.original.documentWorkflowInstanceId,
									});
								}
							}}
						>
							<Icon variant="mail" size="lg" />
						</ActionIcon>
						<ActionIcon
							size="lg"
							variant="subtle"
							color="blue"
							title={tCommonUi('download.download')}
							onClick={(event) => {
								event.stopPropagation();
								if (!dealId || !row.original.fileId || !row.original.fileName) {
									return;
								}
								void downloadFile({
									fileId: row.original.fileId,
									dealId,
									fileName: row.original.fileName,
								});
							}}
						>
							<Icon variant="download-v3" size="lg" />
						</ActionIcon>
						<ActionIcon
							size="lg"
							variant="subtle"
							color="blue"
							title={tCommonUi('common_buttons.view_details')}
							onClick={(event) => {
								event.stopPropagation();
								if (!row.original.fileId || !row.original.fileName) {
									return;
								}
								handlePreviewFile(row.original.fileId, row.original.fileName);
							}}
						>
							<Icon variant="eye" size="lg" />
						</ActionIcon>
					</Flex>
				),
			},
		],
		[
			tMipManage,
			tCommonUi,
			getInvestmentReference,
			navigateToMessages,
			dealId,
			mipId,
			downloadFile,
			handlePreviewFile,
		]
	);

	const onRowClick = (row: InvestorDocumentWorkflowDocumentRes) => {
		if (!dealId || !mipId) {
			return;
		}

		// Does not have any document workflow instance information so we just preview the file
		if (row.isPlainDocument) {
			if (row.fileId && row.fileName) {
				handlePreviewFile(row.fileId, row.fileName);
			}
			return;
		}

		if (!row.documentWorkflowInstanceId) {
			return;
		}
		const path = mipManagePath(MIP_MANAGE_ROUTES.DOCUMENT_DETAILS, {
			dealId,
			mipId,
			documentWorkflowInstanceId: row.documentWorkflowInstanceId,
		});
		navigate(path);
	};

	const TableTitleContent = (
		<Text fz="lg" fw={800}>
			{tMipManage('investors.expanded_row.contracts')}
		</Text>
	);

	return (
		<div ref={ref} id="investor-document-workflow-table">
			{documentsQuery.isLoading ? <InlineLoader /> : null}
			<TableTitle>{TableTitleContent}</TableTitle>
			{documents.length ? (
				<DocumentTable
					data={documents}
					columns={columns}
					dataOperations={dataOperations}
					rowCount={documents.length}
					enablePagination={false}
					enableRowSelection={false}
					hasNoTopToolbar
					enableTopToolbar={false}
					getRowId={(row) => row.documentWorkflowInstanceId}
					onRowClick={(row) => onRowClick(row.original)}
					enableStickyFooter={false}
					enableStickyHeader={false}
					manualSorting={false}
					enableColumnResizing={true}
				/>
			) : (
				<Flex maw="60rem">
					<InfoBox
						iconVariant="information"
						alignContent="center"
						title={tMipManage('investors.details.alerts.contract_title')}
						description={tMipManage('investors.details.alerts.contract_description')}
					/>
				</Flex>
			)}

			{previewDocument && (
				<DocumentViewerModalV2
					opened
					file={previewDocument.fileBlob}
					fileName={previewDocument.fileName}
					fileExtension={previewDocument.fileExtension}
					isLoading={!previewDocument.fileBlob}
					onClose={() => setPreviewDocument(null)}
				/>
			)}
		</div>
	);
});
